// QR codes (ISO/IEC 18004) of a short text: bytes in byte mode, error correction level M (15 % of the
// code may be lost), versions 1 to 10 (up to 213 bytes). For the access code: a phone's camera reads it
// off the screen. The native client's, the package qr of XQ, is the same, module for module.

/** The data codewords of versions 1..10 at level M, its error correction codewords per block and its
 * blocks. */
const DATA = [16, 28, 44, 64, 86, 108, 124, 154, 182, 216];
const EC = [10, 16, 26, 18, 24, 16, 18, 22, 22, 26];
const BLOCKS = [1, 1, 1, 2, 2, 4, 4, 4, 5, 5];
/** Where the alignment patterns' centres are, by version. */
const ALIGN = [[], [6, 18], [6, 22], [6, 26], [6, 30], [6, 34], [6, 22, 38], [6, 24, 42], [6, 26, 46], [6, 28, 50]];

/** x·y in GF(2⁸) of x⁸ + x⁴ + x³ + x² + 1. */
function mul(x: number, y: number): number {
  let z = 0;
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z;
}

/** The generator polynomial of `n` error correction codewords, its leading 1 left out. */
function divisor(n: number): number[] {
  const d = new Array<number>(n).fill(0);
  d[n - 1] = 1;
  let root = 1;
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      d[j] = mul(d[j], root);
      if (j + 1 < n) d[j] ^= d[j + 1];
    }
    root = mul(root, 2);
  }
  return d;
}

/** The error correction codewords of `data` (Reed–Solomon over GF(2⁸)). */
export function correction(data: number[], n: number): number[] {
  const d = divisor(n);
  const r = new Array<number>(n).fill(0);
  for (const b of data) {
    const factor = b ^ r.shift()!;
    r.push(0);
    d.forEach((c, i) => (r[i] ^= mul(c, factor)));
  }
  return r;
}

/** The 15 bits of format information of level M and mask `mask`: BCH, then the mask 0x5412. */
export function formatBits(mask: number): number {
  const data = (0 << 3) | mask;
  let rem = data;
  for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
  return ((data << 10) | rem) ^ 0x5412;
}

/** The 18 bits of version information (versions 7 and up). */
export function versionBits(v: number): number {
  let rem = v;
  for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
  return (v << 12) | rem;
}

/** The codewords of `bytes` at version `v`: mode, count, data, terminator, padding; then each block's
 * error correction, and all interleaved. */
function codewords(bytes: number[], v: number): number[] {
  const bits: number[] = [];
  const put = (value: number, n: number) => {
    for (let i = n - 1; i >= 0; i--) bits.push((value >>> i) & 1);
  };
  put(4, 4);
  put(bytes.length, v < 10 ? 8 : 16);
  for (const b of bytes) put(b, 8);
  const capacity = DATA[v - 1] * 8;
  put(0, Math.min(4, capacity - bits.length));
  put(0, (8 - (bits.length % 8)) % 8);
  for (let pad = 0xec; bits.length < capacity; pad ^= 0xec ^ 0x11) put(pad, 8);
  const data: number[] = [];
  for (let i = 0; i < bits.length; i += 8) data.push(bits.slice(i, i + 8).reduce((a, b) => (a << 1) | b, 0));
  // Blocks: the short ones first, the long ones a codeword more.
  const n = BLOCKS[v - 1];
  const short = Math.floor(DATA[v - 1] / n);
  const longs = DATA[v - 1] % n;
  const blocks: number[][] = [];
  for (let i = 0, at = 0; i < n; i++) {
    const len = short + (i >= n - longs ? 1 : 0);
    blocks.push(data.slice(at, at + len));
    at += len;
  }
  const ecs = blocks.map((b) => correction(b, EC[v - 1]));
  const out: number[] = [];
  for (let i = 0; i <= short; i++) for (const b of blocks) if (i < b.length) out.push(b[i]);
  for (let i = 0; i < EC[v - 1]; i++) for (const e of ecs) out.push(e[i]);
  return out;
}

const MASKS: ((x: number, y: number) => boolean)[] = [
  (x, y) => (x + y) % 2 === 0,
  (_, y) => y % 2 === 0,
  (x) => x % 3 === 0,
  (x, y) => (x + y) % 3 === 0,
  (x, y) => (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0,
  (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
  (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
  (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0,
];

/** How bad a code looks to a reader (the standard's four rules): runs, blocks, finder-like patterns,
 * and dark and light out of balance. */
function penalty(m: boolean[][]): number {
  const size = m.length;
  let p = 0;
  const lines: boolean[][] = [];
  for (let i = 0; i < size; i++) {
    lines.push(m[i]);
    lines.push(m.map((row) => row[i]));
  }
  for (const line of lines) {
    for (let i = 0; i < size; ) {
      let j = i;
      while (j < size && line[j] === line[i]) j++;
      if (j - i >= 5) p += 3 + (j - i - 5);
      i = j;
    }
    const s = line.map((d) => (d ? "1" : "0")).join("");
    for (let i = s.indexOf("1011101"); i >= 0; i = s.indexOf("1011101", i + 1)) {
      const before = s.slice(Math.max(0, i - 4), i);
      const after = s.slice(i + 7, i + 11);
      if (i < 4 || before === "0000" || i + 11 > size || after === "0000") p += 40;
    }
  }
  for (let y = 0; y + 1 < size; y++)
    for (let x = 0; x + 1 < size; x++) {
      const d = m[y][x];
      if (m[y][x + 1] === d && m[y + 1][x] === d && m[y + 1][x + 1] === d) p += 3;
    }
  const dark = m.reduce((n, row) => n + row.filter((d) => d).length, 0);
  p += 10 * Math.floor(Math.abs((dark * 20) / (size * size) - 10));
  return p;
}

/** The QR code of `text`: rows of modules, true dark; `null` if it is too long. */
export function qr(text: string): boolean[][] | null {
  const bytes = [...new TextEncoder().encode(text)];
  const v = DATA.findIndex((d, i) => d - (i < 9 ? 2 : 3) >= bytes.length) + 1;
  if (v === 0) return null;
  const size = 17 + 4 * v;
  const m = Array.from({ length: size }, () => new Array<boolean>(size).fill(false));
  const fixed = Array.from({ length: size }, () => new Array<boolean>(size).fill(false));
  const set = (x: number, y: number, dark: boolean) => {
    m[y][x] = dark;
    fixed[y][x] = true;
  };
  // Timing patterns, then finders with their separators, alignment patterns, the dark module.
  for (let i = 0; i < size; i++) {
    set(6, i, i % 2 === 0);
    set(i, 6, i % 2 === 0);
  }
  for (const [cx, cy] of [[3, 3], [size - 4, 3], [3, size - 4]])
    for (let dy = -4; dy <= 4; dy++)
      for (let dx = -4; dx <= 4; dx++) {
        const x = cx + dx;
        const y = cy + dy;
        const d = Math.max(Math.abs(dx), Math.abs(dy));
        if (x >= 0 && x < size && y >= 0 && y < size) set(x, y, d !== 2 && d !== 4);
      }
  const a = ALIGN[v - 1];
  const last = a[a.length - 1];
  for (const ay of a)
    for (const ax of a) {
      if ((ax === 6 && ay === 6) || (ax === 6 && ay === last) || (ax === last && ay === 6)) continue;
      for (let dy = -2; dy <= 2; dy++)
        for (let dx = -2; dx <= 2; dx++) set(ax + dx, ay + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
    }
  // Format (and version) information: reserved now, written once the mask is chosen.
  const format = (bits: number) => {
    const bit = (i: number) => ((bits >>> i) & 1) === 1;
    for (let i = 0; i <= 5; i++) set(8, i, bit(i));
    set(8, 7, bit(6));
    set(8, 8, bit(7));
    set(7, 8, bit(8));
    for (let i = 9; i < 15; i++) set(14 - i, 8, bit(i));
    for (let i = 0; i < 8; i++) set(size - 1 - i, 8, bit(i));
    for (let i = 8; i < 15; i++) set(8, size - 15 + i, bit(i));
    set(8, size - 8, true);
  };
  format(0);
  if (v >= 7) {
    const bits = versionBits(v);
    for (let i = 0; i < 18; i++) {
      const dark = ((bits >>> i) & 1) === 1;
      set(size - 11 + (i % 3), Math.floor(i / 3), dark);
      set(Math.floor(i / 3), size - 11 + (i % 3), dark);
    }
  }
  // The codewords, two columns at a time from the right, up and down by turns.
  const words = codewords(bytes, v);
  let i = 0;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let vert = 0; vert < size; vert++)
      for (let j = 0; j < 2; j++) {
        const x = right - j;
        const y = ((right + 1) & 2) === 0 ? size - 1 - vert : vert;
        if (!fixed[y][x] && i < words.length * 8) {
          m[y][x] = ((words[i >>> 3] >>> (7 - (i & 7))) & 1) === 1;
          i++;
        }
      }
  }
  // The mask that looks best.
  const plain = m.map((row) => [...row]);
  let best: { p: number; rows: boolean[][] } | null = null;
  for (let k = 0; k < 8; k++) {
    for (let y = 0; y < size; y++) m[y] = plain[y].map((d, x) => (fixed[y][x] ? d : d !== MASKS[k](x, y)));
    format(formatBits(k));
    const p = penalty(m);
    if (best === null || p < best.p) best = { p, rows: m.map((row) => [...row]) };
  }
  return best!.rows;
}
